import React from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { notifyGlobal } from '../services/ServiceUtils';

import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography
} from '@mui/material';

import {
  IconMenu2,
  IconCategory,
  IconDatabaseCog,
  IconUsers,
  IconLogout,
  IconTestPipe,
  IconListDetails,
  IconAlertCircle,
  IconSettings,
  IconSeo,
  IconCreditCard,
  IconCurrencyDollar
} from '@tabler/icons-react';

const drawerWidth = 240;

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { isLoggedIn, userRole } = useAuth();
  const navigate = useNavigate();

  // Redirect if not logged in or not admin
  React.useEffect(() => {
    if (!isLoggedIn || userRole !== 'ADMIN') {
      navigate('/');
    }
  }, [isLoggedIn, userRole, navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const showTestAlert = () => {
    const confirmed = window.confirm('Czy alert dialogi działają poprawnie?');
    if (confirmed) {
      notifyGlobal({ level: 'success', message: 'Świetnie! Alerty działają poprawnie.' });
    } else {
      notifyGlobal({ level: 'warning', message: 'Zgłoś problem deweloperowi.' });
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Admin Panel
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/admin/cache">
            <ListItemIcon>
              <IconDatabaseCog />
            </ListItemIcon>
            <ListItemText primary="Cache Management" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/admin/categories">
            <ListItemIcon>
              <IconCategory />
            </ListItemIcon>
            <ListItemText primary="Categories" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/admin/users">
            <ListItemIcon>
              <IconUsers />
            </ListItemIcon>
            <ListItemText primary="Users" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/admin/listings">
            <ListItemIcon>
              <IconListDetails />
            </ListItemIcon>
            <ListItemText primary="Listings" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/admin/alerts">
            <ListItemIcon>
              <IconAlertCircle />
            </ListItemIcon>
            <ListItemText primary="Alert Dialogs" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/admin/settings/seo">
            <ListItemIcon>
              <IconSeo />
            </ListItemIcon>
            <ListItemText primary="SEO Settings" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/admin/payments">
            <ListItemIcon>
              <IconCreditCard />
            </ListItemIcon>
            <ListItemText primary="Payment Management" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/admin/pricing">
            <ListItemIcon>
              <IconCurrencyDollar />
            </ListItemIcon>
            <ListItemText primary="Pricing Management" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/admin/settings">
            <ListItemIcon>
              <IconSettings />
            </ListItemIcon>
            <ListItemText primary="All Settings" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/admin/test">
            <ListItemIcon>
              <IconTestPipe />
            </ListItemIcon>
            <ListItemText primary="Auth Test" />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/">
            <ListItemIcon>
              <IconLogout />
            </ListItemIcon>
            <ListItemText primary="Back to Site" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <IconMenu2 />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Admin Panel
          </Typography>
          <Button
            color="inherit"
            onClick={showTestAlert}
            sx={{ ml: 2 }}
          >
            Test Alert
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
